/**
 * Changing the password of the signed-in account.
 *
 * Distinct from the forgotten-password flow (PasswordForgotten /
 * PasswordReset), which works without being signed in and goes out by email.
 */
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Stack, Button, IconButton, OutlinedInput, InputLabel, InputAdornment, FormControl, Typography, Box, Paper, Snackbar, Alert } from '@mui/material';
import { LockReset, Visibility, VisibilityOff } from "@mui/icons-material";
import { flattenDjangoRestError, copy as userCopy } from "../utils/userFacingMessages";

function PasswordChange() {
  const navigate = useNavigate();

  const [password1, setPassword1] = useState("");
  const [password2, setPassword2] = useState("");
  const [showNewPassword1, setShowNewPassword1] = useState(false);
  const [showNewPassword2, setShowNewPassword2] = useState(false);

  const [oldPassword, setOldPassword] = useState("");
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastSeverity, setToastSeverity] = useState("success");
  const [toastMessage, setToastMessage] = useState("");
  const authConfig = () => ({
    headers: {
      Authorization: "Token " + (localStorage.getItem("token") || ""),
    },
  });

  const handlePassword1Change = (event) => {
    setPassword1(event.target.value);
  };
  const handlePassword2Change = (event) => {
    setPassword2(event.target.value);
  };
  const handleOldPasswordChange = (event) => {
    setOldPassword(event.target.value);
  };
  const handleClickShowOldPassword = () => {
    setShowOldPassword(!showOldPassword)
  };
  const handleMouseDownOldPassword = (event) => {
    event.preventDefault();
  };
  const handleClickShowNewPassword1 = () => {
    setShowNewPassword1(!showNewPassword1)
  };
  const handleClickShowNewPassword2 = () => {
    setShowNewPassword2(!showNewPassword2)
  };
  const handleMouseDownPassword1 = (event) => {
    event.preventDefault();
  };
  const handleMouseDownPassword2 = (event) => {
    event.preventDefault();
  };
  const showToast = (message, severity = "success") => {
    setToastMessage(message);
    setToastSeverity(severity);
    setToastOpen(true);
  };
  const handleCloseToast = (_, reason) => {
    if (reason === "clickaway") return;
    setToastOpen(false);
  };

  let handleSubmit = async (e) => {
    e.preventDefault();
    if (!(oldPassword || "").trim()) {
      showToast(userCopy.passwordChangeCurrentRequired, "error");
      return;
    }
    const data = {
      new_password1: password1,
      new_password2: password2,
      old_password: oldPassword,
    };
    try {
      const res = await axios.post("oraclase/auth/password/change/", data, authConfig());
      if (res.status === 200) {
        showToast(userCopy.passwordUpdated, "success");
        setTimeout(() => {
          navigate("/profile");
          window.location.reload();
        }, 700);
      } else {
        showToast(userCopy.passwordChangeFailed, "error");
      }
    } catch (err) {
      console.error("Password change failed", err);
      const status = err?.response?.status;
      const raw = err?.response?.data;
      let msg = flattenDjangoRestError(raw);
      if (!msg && status === 500) {
        msg = userCopy.profileSaveServer;
      }
      if (!msg && !err.response) {
        msg = userCopy.profileSaveNetwork;
      }
      if (!msg) {
        msg = userCopy.passwordChangeFailed;
      }
      showToast(msg, "error");
    }
  };
  return (

    <Stack
      direction='column'
      justifyContent='center'
      alignItems='center'
      spacing={4}
      marginTop='2%'
    >
      <Box sx={{ width: "100%", maxWidth: "1150px", px: 2, boxSizing: "border-box" }}>
        <Stack
          sx={{
            width: "100%",
            py: 2,
            borderRadius: 3,
            background: "linear-gradient(135deg, #f5f6f8 0%, #ecedf1 100%)",
            border: "1px solid #dde1e7",
            boxSizing: "border-box",
          }}
        >
          <Box sx={{ px: 2 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              Change Password
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Update your password to keep your account secure.
            </Typography>
          </Box>
        </Stack>

        <Paper
          elevation={0}
          sx={{
            width: "100%",
            mt: 4,
            mb: 4,
            borderRadius: 3,
            border: "1px solid #e7ebf3",
            backgroundColor: "#ffffff",
            p: { xs: 2, md: 3 },
            boxSizing: "border-box",
          }}
        >
          <Stack spacing={2} sx={{ maxWidth: "540px" }}>
            <FormControl fullWidth variant="outlined">
              <InputLabel>Old password</InputLabel>
              <OutlinedInput
                required={true}
                label="Old password"
                id="oldPassword"
                type={showOldPassword ? 'text' : 'password'}
                value={oldPassword}
                onChange={handleOldPasswordChange}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle old password visibility"
                      onClick={handleClickShowOldPassword}
                      onMouseDown={handleMouseDownOldPassword}
                      edge="end"
                    >
                      {showOldPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                }
              />
            </FormControl>

            <FormControl fullWidth variant="outlined">
              <InputLabel>Enter new password</InputLabel>
              <OutlinedInput
                required={true}
                label="New password"
                id="password1"
                type={showNewPassword1 ? 'text' : 'password'}
                value={password1}
                onChange={handlePassword1Change}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle new password visibility"
                      onClick={handleClickShowNewPassword1}
                      onMouseDown={handleMouseDownPassword1}
                      edge="end"
                    >
                      {showNewPassword1 ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                }
              />
            </FormControl>

            <FormControl fullWidth variant="outlined">
              <InputLabel>Repeat new password</InputLabel>
              <OutlinedInput
                required={true}
                label="Repeat new password"
                id="password2"
                type={showNewPassword2 ? 'text' : 'password'}
                value={password2}
                onChange={handlePassword2Change}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle repeated password visibility"
                      onClick={handleClickShowNewPassword2}
                      onMouseDown={handleMouseDownPassword2}
                      edge="end"
                    >
                      {showNewPassword2 ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                }
              />
            </FormControl>

            <Button
              variant="contained"
              startIcon={<LockReset />}
              sx={{
                height: "52px",
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 700,
                alignSelf: { xs: "stretch", sm: "flex-start" },
                px: 3,
              }}
              onClick={handleSubmit}
            >
              Change password
            </Button>
          </Stack>
        </Paper>
      </Box>
      <Snackbar
        open={toastOpen}
        autoHideDuration={3500}
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={handleCloseToast} severity={toastSeverity} sx={{ width: "100%" }} variant="filled">
          {toastMessage}
        </Alert>
      </Snackbar>
    </Stack>

  )
}

export default PasswordChange;
